/**
 * Nákres „dielec v príreze" — ako kus reálne leží na doske, ktorú stroj
 * upne. Pri predreze je kus menší než prírez, pri združení je ich tam
 * viac vedľa seba, pri rotácii je kus v príreze otočený o 90°.
 *
 * Dielenské pravidlo (viď `lib/cncPrirez.ts:rozlozKusy`): kus nikdy nie je
 * v strede — sedí na hrane využiteľnej plochy, aby sa dal kolíkovať zo
 * strany. Dokola prírezu je **pásmo orezu** (nadmiera/2 na stranu) — hrana,
 * za ktorou je len orez, zostáva prístupná, preto je v nákrese zelená.
 */

import type { JSX } from "react";

import { hranyPristupne, rozlozKusy, type CncPrirez } from "@/lib/cncPrirez";
import { normalizeNum } from "@/lib/format";

// Farby zosúladené s rozkresom porezu (templates/porez_plan.html)
const FARBA_KUS = "#fbe3e4";
const FARBA_KUS_OBRYS = "#E32127";
const FARBA_KUS_TEXT = "#7a1114";
const FARBA_PRIREZ = "#94a3b8";
const FARBA_OREZ = "#e2e8f0"; // pásmo orezu (nadmiera) — odreže sa po CNC
const FARBA_VOLA = "#64748b";
const FARBA_OKRAJ = "#16a34a"; // prístupná hrana — kolíkovanie zo strany

interface Props {
  prirez: CncPrirez;
  /** Čisté rozmery dielca (mm) — kus vnútri prírezu. */
  cistaX: number;
  cistaY: number;
  /** Celková nadmiera na rozmer (mm) — pásmo orezu je polovica na stranu. */
  nadmieraXMm?: number;
  nadmieraYMm?: number;
  kod?: string | null;
  /** Smer vlákien dielca ("X" / "Y" / "0") — kreslí sa šípka. */
  smerVlakien?: string | null;
  /** Menší nákres do bočného panelu. */
  kompaktny?: boolean;
  /** Pevná šírka v px namiesto `w-full` (drží výšku riadku v gride). */
  sirkaPx?: number;
}

/** Kóta — text len keď je hodnota nad `min` (inak by len zavadzala). */
function Kota({
  x, y, hodnota, popis, min,
}: {
  x: number;
  y: number;
  hodnota: number;
  popis: string;
  min: number;
}): JSX.Element | null {
  if (hodnota <= min) return null;
  return (
    <text
      x={x}
      y={y}
      fill={FARBA_VOLA}
      fontSize={11}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {popis} {normalizeNum(Math.round(hodnota * 10) / 10)}
    </text>
  );
}

export function PrirezVisual({
  prirez, cistaX, cistaY, nadmieraXMm = 0, nadmieraYMm = 0,
  kod, smerVlakien, kompaktny = false, sirkaPx,
}: Props): JSX.Element | null {
  const kusy = rozlozKusy(prirez, cistaX, cistaY, nadmieraXMm, nadmieraYMm);
  if (kusy.length === 0 || prirez.x <= 0 || prirez.y <= 0) return null;

  const VW = kompaktny ? 260 : 380;
  const VH = kompaktny ? 190 : 260;
  const pad = 28;
  const s = Math.min((VW - 2 * pad) / prirez.x, (VH - 2 * pad) / prirez.y);
  const w = prirez.x * s;
  const h = prirez.y * s;
  // vycentruj prírez v ráme; y=0 je dole (SVG rastie dole)
  const ox = (VW - w) / 2;
  const oy = (VH - h) / 2;
  const X = (mm: number): number => ox + mm * s;
  const Y = (mm: number): number => oy + h - mm * s;

  // pásmo orezu — nadmiera je celková na rozmer, polovica na každú stranu
  const padX = nadmieraXMm / 2;
  const padY = nadmieraYMm / 2;
  // vlákno beží po X dielca, ak nie je smer "Y"
  const vlaknoPoX = smerVlakien !== "Y";

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      className={sirkaPx == null ? "w-full" : undefined}
      width={sirkaPx}
      height={sirkaPx == null ? undefined : (sirkaPx * VH) / VW}
      role="img"
      aria-label="Dielec v príreze"
      data-testid="prirez-visual"
    >
      {/* prírez — vyplnený farbou orezu, obrys prerušovaný */}
      <rect
        x={ox}
        y={oy}
        width={w}
        height={h}
        fill={FARBA_OREZ}
        stroke={FARBA_PRIREZ}
        strokeWidth={1.5}
        strokeDasharray="5 3"
      />
      {/* využiteľná plocha — biely obdĺžnik nechá orez vyniknúť ako rámik */}
      {padX > 0 || padY > 0 ? (
        <rect
          x={X(padX)}
          y={Y(prirez.y - padY)}
          width={(prirez.x - 2 * padX) * s}
          height={(prirez.y - 2 * padY) * s}
          fill="#ffffff"
          data-testid="prirez-plocha"
        />
      ) : null}
      <text
        x={VW / 2}
        y={oy - 9}
        fill={FARBA_PRIREZ}
        fontSize={11}
        textAnchor="middle"
      >
        prírez {normalizeNum(prirez.x)}×{normalizeNum(prirez.y)}
      </text>

      {kusy.map((kus, i) => {
        const okraje = hranyPristupne(kus, prirez, nadmieraXMm, nadmieraYMm);
        const kx = X(kus.x);
        const ky = Y(kus.y + kus.sirkaY);
        const kw = kus.sirkaX * s;
        const kh = kus.sirkaY * s;
        // prístupné hrany → kolíkovanie zo strany (pred nimi je len orez)
        const okrajCiary: [number, number, number, number][] = [];
        if (okraje.vlavo) okrajCiary.push([kx, ky, kx, ky + kh]);
        if (okraje.vpravo) okrajCiary.push([kx + kw, ky, kx + kw, ky + kh]);
        if (okraje.dole) okrajCiary.push([kx, ky + kh, kx + kw, ky + kh]);
        if (okraje.hore) okrajCiary.push([kx, ky, kx + kw, ky]);
        const popisSaZmesti = kw >= 54 && kh >= 26;
        return (
          <g key={i} data-testid="prirez-kus">
            <rect
              x={kx}
              y={ky}
              width={kw}
              height={kh}
              fill={FARBA_KUS}
              stroke={FARBA_KUS_OBRYS}
              strokeWidth={1.2}
            />
            {/* smer vlákien — pri otočenom kuse beží naprieč prírezom */}
            {kw >= 30 && kh >= 16 ? (
              <text
                x={kx + kw / 2}
                y={ky + kh / 2 + (popisSaZmesti ? 13 : 4)}
                fill={FARBA_KUS_TEXT}
                fontSize={10}
                textAnchor="middle"
                dominantBaseline="middle"
                opacity={0.75}
              >
                {(vlaknoPoX ? !kus.otoceny : kus.otoceny) ? "↔" : "↕"}
                {kus.otoceny ? " ↻" : ""}
              </text>
            ) : null}
            {popisSaZmesti ? (
              <text
                x={kx + kw / 2}
                y={ky + kh / 2 - 4}
                fill={FARBA_KUS_TEXT}
                fontSize={11}
                fontWeight={600}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {kod ? `${kod} ` : ""}
                {normalizeNum(cistaX)}×{normalizeNum(cistaY)}
              </text>
            ) : null}
            {okrajCiary.map(([x1, y1, x2, y2], j) => (
              <line
                key={j}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={FARBA_OKRAJ}
                strokeWidth={3}
                strokeLinecap="round"
              />
            ))}
          </g>
        );
      })}

      {/* Kóty: reálna vôľa (nad pásmo orezu) + medzera medzi kusmi */}
      {(() => {
        const prvy = kusy[0]!;
        const posledny = kusy[kusy.length - 1]!;
        const volaX = prirez.x - (posledny.x + posledny.sirkaX);
        const volaY = prirez.y - (posledny.y + posledny.sirkaY);
        const medzeraJePoX = kusy.length > 1 && kusy[1]!.x > prvy.x;
        const medzera =
          kusy.length > 1
            ? medzeraJePoX
              ? kusy[1]!.x - (prvy.x + prvy.sirkaX)
              : kusy[1]!.y - (prvy.y + prvy.sirkaY)
            : 0;
        return (
          <>
            {/* min = pásmo orezu → samotný orez sa ako „vôľa" nepopisuje
                (inak by sa popisok bil s rozmerom prírezu nad nákresom) */}
            <Kota
              x={X(posledny.x + posledny.sirkaX + volaX / 2)}
              y={Y(prirez.y / 2)}
              hodnota={volaX}
              popis="vôľa"
              min={padX + 1}
            />
            <Kota
              x={X(prirez.x / 2)}
              y={Y(posledny.y + posledny.sirkaY + volaY / 2)}
              hodnota={volaY}
              popis="vôľa"
              min={padY + 1}
            />
            <Kota
              x={
                medzeraJePoX
                  ? X(prvy.x + prvy.sirkaX + medzera / 2)
                  : X(prirez.x / 2)
              }
              y={
                medzeraJePoX
                  ? Y(prirez.y / 2)
                  : Y(prvy.y + prvy.sirkaY + medzera / 2)
              }
              hodnota={medzera}
              popis="medzera"
              min={0}
            />
          </>
        );
      })()}
    </svg>
  );
}

/** Legenda k nákresu — vysvetlí zelené hrany, pásmo orezu a `↻`. */
export function PrirezLegenda({
  kompaktna = false,
}: {
  kompaktna?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
      <span className="flex items-center gap-1.5">
        <span
          className="inline-block h-0.5 w-4 rounded"
          style={{ background: FARBA_OKRAJ }}
        />
        hrana prístupná na kolíkovanie
      </span>
      {kompaktna ? null : (
        <>
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-4 rounded-sm border"
              style={{ background: FARBA_OREZ, borderColor: FARBA_PRIREZ }}
            />
            pásmo orezu (nadmiera na stranu)
          </span>
          <span>↻ kus otočený o 90°</span>
        </>
      )}
    </div>
  );
}
